// src/pages/inventory/ProductImportHistory.jsx
import { useEffect, useState } from "react";
import ThemeMaker from "../../components/thememaker";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import LoaderSpiner from "../../components/LoaderSpiner/LoaderSpiner";
import { FaDownload } from "react-icons/fa6";
import Swal from "sweetalert2";
import { httpService } from "../../services/httpService";
import DynamicTable from "../../components/common/DynamicTable";
import { format } from "date-fns";

const ProductImportHistory = () => {
  const navigate = useNavigate();
  const [importHistory, setImportHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleGetImportHistory = async () => {
    try {
      setLoading(true);
      const response = await httpService.get("/vendorproduct/import-history");
      if (response?.status) {
        setImportHistory(response?.data);
      }
    } catch (error) {
      console.error("Error fetching import history:", error);
      toast.error("Failed to fetch import history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleGetImportHistory();
  }, []);

  const convertRowsToCSV = (rows) => {
    if (!rows || !rows.length) return "";

    const flatData = rows.map((r) => ({
      row_number: r.row_number,
      ...r.data,
      notes: r.notes || "",
      status: r.processed ? "Processed" : "Failed",
    }));

    const headers = Object.keys(flatData[0]);
    const csvRows = [
      headers.join(","),
      ...flatData.map((row) =>
        headers.map((h) => JSON.stringify(row[h] ?? "")).join(",")
      ),
    ];

    return csvRows.join("\n");
  };

  const downloadCSVFile = (filename, csvContent) => {
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleImportUndo = async (id) => {
    try {
      const confirmation = await Swal.fire({
        title: "Are you sure?",
        text: "This will undo the import and cannot be reversed.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, undo it!",
        cancelButtonText: "Cancel",
      });

      if (confirmation.isConfirmed) {
        const response = await httpService.post("/vendorproduct/undo-import", {
          id: id,
        });

        if (response.status) {
          handleGetImportHistory();
          await Swal.fire({
            title: "Success",
            text: "Import has been undone successfully!",
            icon: "success",
            confirmButtonText: "OK",
            timer: 1500,
          });
        } else {
          await Swal.fire({
            title: "Error",
            text: "Failed to undo the import. Please try again later.",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }
    } catch (error) {
      console.error("Import undo error", error);
      toast.error("Failed to undo import");
    }
  };

  const columns = [
    {
      key: "createdAt",
      header: "Date",
      sortable: true,
      renderCell: (row) => format(new Date(row.createdAt), "dd-MM-yyyy"),
    },
    {
      key: "file_name",
      header: "File Name",
      sortable: false,
      renderCell: (row) => row?.file_name || "—",
    },
    {
      key: "import_type",
      header: "Type",
      sortable: false,
      renderCell: (row) => row?.import_type || "Product",
    },
    {
      key: "total_rows",
      header: "Total Rows",
      sortable: true,
      renderCell: (row) => row?.total_rows || 0,
    },
    {
      key: "success_count",
      header: "Processed",
      sortable: true,
      renderCell: (row) => (
        <div className="flex items-center gap-2">
          <span>{row?.success_count || 0}</span>
          {row?.success_count > 0 && row?.processed?.length > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                const csv = convertRowsToCSV(row.processed);
                downloadCSVFile(`${row.file_name}-processed.csv`, csv);
              }}
              className="p-1 hover:bg-green-100 rounded transition-colors"
              title="Download processed records"
            >
              <FaDownload className="w-4 h-4 text-green-600" />
            </button>
          )}
        </div>
      ),
    },
    {
      key: "warning_count",
      header: "Warnings",
      sortable: true,
      renderCell: (row) => (
        <div className="flex items-center gap-2">
          <span>{row?.warning_count || 0}</span>
          {row?.warning_count > 0 && row?.warnings?.length > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                const csv = convertRowsToCSV(row.warnings);
                downloadCSVFile(`${row.file_name}-warnings.csv`, csv);
              }}
              className="p-1 hover:bg-yellow-100 rounded transition-colors"
              title="Download warnings"
            >
              <FaDownload className="w-4 h-4 text-yellow-600" />
            </button>
          )}
        </div>
      ),
    },
    {
      key: "error_count",
      header: "Errors",
      sortable: true,
      renderCell: (row) => (
        <div className="flex items-center gap-2">
          <span>{row?.error_count || 0}</span>
          {row?.error_count > 0 && row?.errors?.length > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                const csv = convertRowsToCSV(row.errors);
                downloadCSVFile(`${row.file_name}-errors.csv`, csv);
              }}
              className="p-1 hover:bg-red-100 rounded transition-colors"
              title="Download errors"
            >
              <FaDownload className="w-4 h-4 text-red-600" />
            </button>
          )}
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      renderCell: (row) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          row?.status === "completed" ? "bg-green-100 text-green-700" :
          row?.status === "failed" ? "bg-red-100 text-red-700" :
          "bg-yellow-100 text-yellow-700"
        }`}>
          {row?.status || "Processing"}
        </span>
      ),
    },
  ];

//   const actions = [
//     {
//       label: "Undo Import",
//       icon: <FaDownload className="w-4 h-4 text-red-600" />,
//       onClick: (row) => handleImportUndo(row._id),
//       colorClasses: "hover:border-red-500 hover:bg-red-50",
//     },
//   ];

  return (
    <ThemeMaker>
      {loading && <LoaderSpiner text="Loading..." />}
      
      <div className="p-4">
        <div className="flex flex-wrap justify-between items-center mb-4">
          <div className="flex items-center space-x-6">
            <h1 className="text-2xl font-semibold">Product Import History</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/admin/inventory-management/product-import")}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition duration-200 text-sm font-medium"
            >
              Import Products
            </button>
          </div>
        </div>
        
        <DynamicTable columns={columns} data={importHistory} />
      </div>
    </ThemeMaker>
  );
};

export default ProductImportHistory;